// src/Context/UserContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api, setOnUnauthorized, setTokens, clearTokens, getRefreshToken, getAccessToken } from "../api";
import type { TrialStatus, BillingStatus } from "../api/billing";
import { fetchTrialStatus as fetchTrialStatusAPI, fetchBillingStatus as fetchBillingStatusAPI } from "../api/billing";

/** ---------- Types ---------- */
export interface User {
  alive: boolean; // simple flag saying "we have a valid session"
}

export interface SimplifiedTemplates {
  templateName: string;
  templateURL: string;
  templateDescription: string;
  templateDocumentType: string;
  templateKind?: string;
  dateCreated: string;
  dateLastUpdate: string;
}

export interface SimplifiedContracts {
  contractName: string;
  contractURL: string;
  contractType: string;
  contractDescription: string;
  recipients: [];
  dateSent: string;
  dateCompleted: string;
}

type Profile = {
  name: string;
  email: string;
  loginMethod: string;
} | null;

interface UserContextType {
  user: User | null;
  loading: boolean;
  /** Verifies/refreshes session, then confirms by fetching profile */
  fetchUser: () => Promise<void>;
  /** Returns profile or null if unauthorized/failed (and nukes auth on 401/403) */
  fetchUserProfile: () => Promise<Profile>;
  fetchUserTemplates: () => Promise<{
    templates: SimplifiedTemplates[];
  } | null>;
  // fetchUserContracts?: () => Promise<{ contracts: SimplifiedContracts[] } | null>;
  /** Logs out server-side (best effort) and clears local state */
  logout: () => Promise<void>;
  /** Current trial/billing status (null while loading or if fetch failed) */
  trialStatus: TrialStatus | null;
  /** Re-fetch trial status from the server */
  fetchTrialStatus: () => Promise<void>;
  /** Full billing status with limits and usage */
  billingStatus: BillingStatus | null;
  /** Re-fetch billing status from the server */
  fetchBillingStatus: () => Promise<void>;
}

/** ---------- Context ---------- */
const UserContext = createContext<UserContextType | undefined>(undefined);

/** ---------- Provider ---------- */
export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [trialStatus, setTrialStatus] = useState<TrialStatus | null>(null);
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);

  // Stable identities: pages list these as effect deps, and a new function
  // each render re-ran those effects in a loop (hundreds of API calls).
  const fetchTrialStatus = useCallback(async () => {
    try {
      const data = await fetchTrialStatusAPI();
      setTrialStatus(data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch trial status", err);
    }
  }, []);

  const fetchBillingStatus = useCallback(async () => {
    try {
      const data = await fetchBillingStatusAPI();
      setBillingStatus(data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch billing status", err);
    }
  }, []);

  /** Centralized way to drop all client auth state */
  const nukeAuthState = () => {
    setUser(null);
    clearTokens();
    try {
      sessionStorage.clear();
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    setOnUnauthorized(() => {
      nukeAuthState();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** On first mount: try to refresh + verify (guarded against strict mode double-fire) */
  const bootedRef = React.useRef(false);
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Re-verify session when user returns to the tab after being away */
  useEffect(() => {
    let lastVerified = Date.now();
    const COOLDOWN = 5 * 60 * 1000; // 5 minutes

    const handleVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastVerified > COOLDOWN
      ) {
        lastVerified = Date.now();
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        fetchUser();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Refresh access token, then verify by fetching profile */
  const fetchUser = async () => {
    setLoading(true);
    try {
      // If we have a refresh token, attempt to refresh
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        const refreshRes = await api.post("/api/refresh", { refreshToken });
        const { accessToken: newAccess, refreshToken: newRefresh } = refreshRes.data;
        setTokens(newAccess, newRefresh);
      }

      // If we have no access token at this point, we're not logged in
      if (!getAccessToken()) {
        nukeAuthState();
        return;
      }

      // Verify the refresh actually resulted in a valid access token
      const p = await api.get("/api/fetch-user-profile");
      if (p.status === 200) {
        setUser({ alive: true });
        // Fetch trial/billing status now that we know the session is valid
        fetchTrialStatus();
        fetchBillingStatus();
      } else {
        nukeAuthState();
      }
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        nukeAuthState();
      } else {
        setUser(null);
      }
      // eslint-disable-next-line no-console
      console.error(
        "Failed to fetch user",
        err?.response?.data || err?.message || err
      );
    } finally {
      setLoading(false);
    }
  };

  /** Get minimal profile to gate UI; nuke state on 401/403 */
  const fetchUserProfile = useCallback(async (): Promise<Profile> => {
    try {
      const res = await api.get("/api/fetch-user-profile");
      if (res.status === 200) {
        return {
          name: res.data.name as string,
          email: res.data.email as string,
          loginMethod: res.data.loginMethod as string,
        };
      }
      return null;
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) nukeAuthState();
      // eslint-disable-next-line no-console
      console.error(
        "FetchingUserProfileFailed",
        err?.response?.data || err?.message || err
      );
      return null;
    }
    // nukeAuthState only touches state setters and storage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Example data fetch that also respects auth */
  const fetchUserTemplates = async (): Promise<{
    templates: SimplifiedTemplates[];
  } | null> => {
    try {
      const res = await api.get("/api/fetch-user-templates");
      if (res.status === 200) {
        return { templates: res.data.templates as SimplifiedTemplates[] };
      }
      return null;
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) nukeAuthState();
      // eslint-disable-next-line no-console
      console.error(
        "FetchingUserTemplatesFailed",
        err?.response?.data || err?.message || err
      );
      return null;
    }
  };

  // If/when you need contracts, uncomment and return it from the provider
  // const fetchUserContracts = async (): Promise<{ contracts: SimplifiedContracts[] } | null> => {
  //   try {
  //     const res = await api.get("/api/fetch-user-contracts");
  //     if (res.status === 200) {
  //       return { contracts: res.data.contracts as SimplifiedContracts[] };
  //     }
  //     return null;
  //   } catch (err: any) {
  //     const status = err?.response?.status;
  //     if (status === 401 || status === 403) nukeAuthState();
  //     console.error("FetchingUserContractsFailed", err?.response?.data || err?.message || err);
  //     return null;
  //   }
  // };

  /** Explicit logout */
  const logout = async () => {
    try {
      const refreshToken = getRefreshToken();
      await api.post("/api/logout", { refreshToken });
    } catch (err) {
      // Non-fatal; still clear client state
      // eslint-disable-next-line no-console
      console.error(
        "Logout error",
        (err as any)?.response?.data || (err as any)?.message || err
      );
    } finally {
      nukeAuthState();
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        fetchUser,
        fetchUserProfile,
        fetchUserTemplates,
        // fetchUserContracts,
        logout,
        trialStatus,
        fetchTrialStatus,
        billingStatus,
        fetchBillingStatus,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

/** ---------- Hook ---------- */
export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used inside UserProvider");
  return ctx;
};
